import { useState, useMemo } from 'react';
import { TextField, InputAdornment, FormControl, Select, MenuItem, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer as BarResponsiveContainer,
} from 'recharts';
import styles from './approval_persons_invoice_management.module.css';

// Custom Tooltip Component for Bar Charts
const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles['custom-tooltip']}>
        <div className={styles['tooltip-label']}>{label}</div>
        <div className={styles['tooltip-value']}>${payload[0].value.toLocaleString()}</div>
      </div>
    );
  }
  return null;
};

export const DashboardTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPO, setSelectedPO] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Dummy data for filters
  const poOptions = useMemo(() => ['PO-2024-001', 'PO-2024-002', 'PO-2024-003'], []);
  const vendorOptions = useMemo(() => ['PTCL', 'Wateen', 'Nayatel', 'Softoo'], []);
  const yearOptions = useMemo(() => ['2024', '2023', '2022'], []);

  // Dummy data for pie charts
  const vendorData = [
    { name: 'PTCL', value: 40, color: '#3B82F6' },
    { name: 'Wateen', value: 30, color: '#10B981' },
    { name: 'Nayatel', value: 20, color: '#F59E0B' },
    { name: 'Softoo', value: 10, color: '#EF4444' },
  ];

  const milestoneData = [
    { name: '30%', value: 25, color: '#3B82F6' },
    { name: '50%', value: 30, color: '#10B981' },
    { name: '70%', value: 20, color: '#F59E0B' },
    { name: '100%', value: 25, color: '#EF4444' },
  ];

  const yearlyInvoicesData = [
    { name: '2024', value: 45, color: '#3B82F6' },
    { name: '2023', value: 30, color: '#10B981' },
    { name: '2022', value: 15, color: '#F59E0B' },
    { name: '2021', value: 10, color: '#EF4444' },
  ];

  // Dummy data for bar charts
  const paymentsData = [
    { name: 'Jan', value: 120000 },
    { name: 'Feb', value: 150000 },
    { name: 'Mar', value: 180000 },
    { name: 'Apr', value: 200000 },
    { name: 'May', value: 170000 },
    { name: 'Jun', value: 190000 },
    { name: 'Jul', value: 210000 },
    { name: 'Aug', value: 195000 },
    { name: 'Sep', value: 220000 },
    { name: 'Oct', value: 205000 },
    { name: 'Nov', value: 230000 },
    { name: 'Dec', value: 250000 },
  ];

  const deductionsData = [
    { name: 'Jan', value: 15000 },
    { name: 'Feb', value: 18000 },
    { name: 'Mar', value: 22000 },
    { name: 'Apr', value: 25000 },
    { name: 'May', value: 20000 },
    { name: 'Jun', value: 23000 },
    { name: 'Jul', value: 26000 },
    { name: 'Aug', value: 24000 },
    { name: 'Sep', value: 28000 },
    { name: 'Oct', value: 27000 },
    { name: 'Nov', value: 30000 },
    { name: 'Dec', value: 32000 },
  ];

  // Custom label function to position percentage inside pie chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className={styles['dashboard-container']}>
      {/* Search and Filters Row */}
      <div className={styles['search-filters-row']}>
        <TextField
          placeholder="Search"
          size="small"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          sx={{
            backgroundColor: '#fff',
            width: '300px',
            '& .MuiInputBase-input': {
              color: 'gray',
              '::placeholder': {
                color: '#b3b9c4',
                opacity: 1,
              },
            },
            '& .MuiOutlinedInput-root': {
              border: '1px solid #ccc',
              borderRadius: '10px',
              paddingRight: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* PO Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedPO}
              onChange={(e) => setSelectedPO(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                PO
              </MenuItem>
              {poOptions.map((po, index) => (
                <MenuItem
                  key={po}
                  value={po}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== poOptions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {po}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vendor Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                VENDOR
              </MenuItem>
              {vendorOptions.map((vendor, index) => (
                <MenuItem
                  key={vendor}
                  value={vendor}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== vendorOptions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {vendor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                YEAR
              </MenuItem>
              {yearOptions.map((year, index) => (
                <MenuItem
                  key={year}
                  value={year}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== yearOptions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </div>

      {/* Pie Charts Row */}
      <div className={styles['charts-row']}>
        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Vendor</h3>
          <div className={styles['pie-chart-wrapper']}>
            <ResponsiveContainer width="50%" height={300}>
              <PieChart>
                <Pie
                  data={vendorData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {vendorData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles['pie-legend']}>
              {vendorData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']}>{entry.name}</span>
                  <span className={styles['legend-value']}>{entry.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Milestone</h3>
          <div className={styles['pie-chart-wrapper']}>
            <ResponsiveContainer width="50%" height={300}>
              <PieChart>
                <Pie
                  data={milestoneData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {milestoneData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles['pie-legend']}>
              {milestoneData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']}>{entry.name}</span>
                  <span className={styles['legend-value']}>{entry.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles['pie-chart-container']}>
          <h3 className={styles['chart-title']}>Yearly Invoices</h3>
          <div className={styles['pie-chart-wrapper']}>
            <ResponsiveContainer width="50%" height={300}>
              <PieChart>
                <Pie
                  data={yearlyInvoicesData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {yearlyInvoicesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles['pie-legend']}>
              {yearlyInvoicesData.map((entry, index) => (
                <div key={index} className={styles['legend-item']}>
                  <div
                    className={styles['legend-color']}
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className={styles['legend-label']}>{entry.name}</span>
                  <span className={styles['legend-value']}>{entry.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bar Charts Row */}
      <div className={styles['charts-row']}>
        <div className={styles['chart-container']} style={{ flex: '1 1 50%' }}>
          <h3 className={styles['chart-title']}>Payments</h3>
          <BarResponsiveContainer
            width="100%"
            height={300}
            style={{ outline: 'none' }}
            onClick={(e) => e.preventDefault()}
            onFocus={(e) => e.target.blur()}
          >
            <BarChart
              data={paymentsData}
              style={{ outline: 'none' }}
              onClick={(e) => e.preventDefault()}
            >
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: '#666' }}
                axisLine={{ stroke: '#999' }}
              />
              <YAxis
                domain={[0, 300000]}
                ticks={[0, 40000, 80000, 120000, 160000, 200000, 240000, 280000]}
                allowDecimals={false}
                interval={0}
                allowDataOverflow={false}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: '#666' }}
                axisLine={{ stroke: '#999' }}
              />
              <Tooltip content={<CustomBarTooltip />} cursor={false} />
              <Bar
                dataKey="value"
                fill="#bd1d23"
                barSize={15}
                onMouseEnter={() => {}}
                style={{ cursor: 'default' }}
              />
            </BarChart>
          </BarResponsiveContainer>
        </div>

        <div className={styles['chart-container']} style={{ flex: '1 1 50%' }}>
          <h3 className={styles['chart-title']}>Deductions</h3>
          <BarResponsiveContainer
            width="100%"
            height={300}
            style={{ outline: 'none' }}
            onClick={(e) => e.preventDefault()}
            onFocus={(e) => e.target.blur()}
          >
            <BarChart
              data={deductionsData}
              style={{ outline: 'none' }}
              onClick={(e) => e.preventDefault()}
            >
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: '#666' }}
                axisLine={{ stroke: '#999' }}
              />
              <YAxis
                domain={[0, 38000]}
                ticks={[0, 5000, 10000, 15000, 20000, 25000, 30000, 35000]}
                allowDecimals={false}
                interval={0}
                allowDataOverflow={false}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: '#666' }}
                axisLine={{ stroke: '#999' }}
              />
              <Tooltip content={<CustomBarTooltip />} cursor={false} />
              <Bar
                dataKey="value"
                fill="#3B82F6"
                barSize={15}
                onMouseEnter={() => {}}
                style={{ cursor: 'default' }}
              />
            </BarChart>
          </BarResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
